import { useMemo, useState } from "react"
import { calcStartDate } from "@/utils/calc-start-date-util"
import { analysisStore } from "@/routes/apt/[apt_code]/analysis/analysis-store"
import AptLeakDetailModal from "@/components/detail-modal/apt-leak-detail-modal"
import { periodStore } from "@/components/detail-modal/period-store"
import useIsMobile from "@/hooks/use-is-mobile"
import AlarmModal from "@/routes/apt/[apt_code]/analysis/_components/bill-kpi/alarm/alarm-modal"
import { aptSearchStore } from "@/routes/select/apt/apt-search-store"
import PdfModal from "@/routes/apt/[apt_code]/analysis/_components/bill-kpi/pdf/pdf-modal"
import HouseholdSelector from "@/routes/apt/[apt_code]/analysis/_components/household/household-selector"
import Flex from "@/components/flex"
import WeeklySummary from "@/routes/apt/[apt_code]/analysis/_components/weekly-summary/weekly-summary"
import UsageKpiView from "@/routes/apt/[apt_code]/analysis/_components/usage-kpi/usage-kpi-view"
import BillKpiView from "@/routes/apt/[apt_code]/analysis/_components/bill-kpi/bill-kpi-view"
import LeakChartView from "./_components/leak/leak-chart-view"
import { leakChartStore } from "./_components/leak/leak-chart-store"

const AnalysisPage = () => {
  const { ended_at: end_date } = aptSearchStore((s) => s.pickApt)

  const isMobile = useIsMobile()

  const [leakActive, setLeakActive] = useState("")
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false)

  const { selectedDong, allLeakUnits } = analysisStore()

  const { apiGetLeakChart, series, xLabels, pickUnit, setPickUnit } = leakChartStore()

  const { startDate, endDate, setStartDate, setEndDate } = periodStore()

  const leakUnits = useMemo(() => {
    if (selectedDong === "all") {
      return allLeakUnits
    }

    return allLeakUnits.filter((unit) => Number(unit.dong) === Number(selectedDong))
  }, [allLeakUnits, selectedDong])

  const getLeakDetail = (unit) => {
    const {
      danji,
      dong,
      ho,
      leak_start_at,
      leak_end_at,
      leak_volume,
      usage_volume,
      leak_rate,
      leak_type
    } = unit

    const leakStartAtFormat = leak_start_at.replace(":00:00", "")
    const leakEndAtFormat = leak_end_at.replace(":00:00", "")

    setPickUnit({
      customerKey: `${danji}-${dong}-${ho}`,
      dong,
      ho,
      leakStartAt: leakStartAtFormat,
      leakEndAt: leakEndAtFormat,
      leakAmount: leak_volume,
      usageAmount: usage_volume,
      leakRate: leak_rate,
      leakType: leak_type
    })

    setLeakActive(`${dong}-${ho}`)

    const selectedPeriod = leakChartStore.getState().selectedPeriod ?? "week"

    let start_date = calcStartDate(end_date, selectedPeriod)

    leakChartStore.getState().setSelectedPeriod(selectedPeriod)

    if (selectedPeriod === "leakStartTime") {
      start_date = leak_start_at.slice(0, 10)
    }

    setStartDate(start_date)
    setEndDate(end_date)

    apiGetLeakChart({
      dong,
      ho,
      start_date,
      end_date
    })

    if (window.innerWidth < 768) {
      setIsMobileModalOpen(true)
    }
  }

  const onMobilePeriodSearch = async () => {
    if (!pickUnit) return

    await apiGetLeakChart({
      dong: pickUnit.dong,
      ho: pickUnit.ho,
      start_date: startDate,
      end_date: endDate
    })
  }

  return (
    <>
      <PdfModal />
      <AlarmModal />

      <Flex className="h-full">
        <div className={`h-full grid grid-rows-[minmax(265px,1fr)_3fr] gap-5 md:w-auto w-full`}>
          <WeeklySummary className="w-full md:w-80 p-4 space-y-3" />

          <HouseholdSelector
            className="w-full md:w-80 flex flex-col overflow-hidden"
            households={leakUnits}
            active={leakActive}
            onClick={getLeakDetail}
            indicator={{
              label: {
                ko: "주간 누수율",
                en: "Weekly Leak Rate",
                es: "Tasa de fugas semanal"
              },
              key: "leak_rate",
              unit: "%"
            }}
          />
        </div>

        {!isMobile && (
          <Flex className="flex-1 flex-col">
            <LeakChartView className="flex flex-col flex-1 gap-8" />

            <Flex>
              <UsageKpiView className="flex-1 p-5 space-y-3" />

              <BillKpiView className="flex-1 p-5 flex flex-col h-full" />
            </Flex>
          </Flex>
        )}
      </Flex>

      <AptLeakDetailModal
        isOpen={isMobileModalOpen}
        onClose={() => setIsMobileModalOpen(false)}
        selectedUnit={pickUnit}
        chartData={{ series, xLabels }}
        periodProps={{
          startDate,
          endDate,
          setStartDate,
          setEndDate,
          onSearch: onMobilePeriodSearch
        }}
      />
    </>
  )
}

export default AnalysisPage
